/**
 * SchemaDataStorePropertyPanel Component
 *
 * Custom property panel for schema_data_store workflow nodes.
 * Provides intelligent dropdowns for variable selection and unique fields.
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Node, Edge } from 'reactflow';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ERROR, WARNING, FOREST_GREEN } from '@/styles/tokens';
import {
  getComposeSchemaVariables,
  ComposeSchemaVariable,
} from '@/utils/workflow-graph-analyzer';
import { schemaApi, Schema } from '@/services/api/schemaApi';
import * as errorUtils from '@/utils/errors';

export interface SchemaDataStoreConfig {
  dataVariable?: string;
  operation: 'insert' | 'update' | 'upsert';
  uniqueFields?: string[];
  addMetadata?: boolean;
  resultVariable?: string;
}

interface SchemaDataStorePropertyPanelProps {
  node: Node;
  nodes: Node[];
  edges: Edge[];
  onChange: (config: Partial<SchemaDataStoreConfig>) => void;
  className?: string;
}

export const SchemaDataStorePropertyPanel: React.FC<
  SchemaDataStorePropertyPanelProps
> = ({ node, nodes, edges, onChange, className }) => {
  // State
  const [availableVariables, setAvailableVariables] = useState<
    ComposeSchemaVariable[]
  >([]);
  const [schemaFields, setSchemaFields] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchedSchema, setFetchedSchema] = useState<Schema | null>(null);

  // Extract config from node data
  const config = (node.data?.config || {}) as SchemaDataStoreConfig;

  // On mount: Analyze graph to find upstream compose_schema variables
  useEffect(() => {
    try {
      const variables = getComposeSchemaVariables(nodes, edges, node.id);
      setAvailableVariables(variables);

      // If no variables found and we have a dataVariable configured, show warning
      if (variables.length === 0 && config.dataVariable) {
        setError('The upstream compose_schema node that outputs this variable was not found.');
      }
    } catch (err) {
      console.error('Error analyzing workflow graph:', err);
      setError('Failed to analyze workflow graph');
    }
  }, [nodes, edges, node.id, config.dataVariable]);

  // When dataVariable changes: Fetch schema and populate fields
  useEffect(() => {
    if (!config.dataVariable) {
      setSchemaFields([]);
      setFetchedSchema(null);
      setError(null);
      return;
    }

    fetchSchemaFields(config.dataVariable);
  }, [config.dataVariable]);

  const fetchSchemaFields = useCallback(
    async (variableName: string) => {
      setLoading(true);
      setError(null);

      try {
        // Find compose_schema node that outputs this variable
        const variable = availableVariables.find((v) => v.name === variableName);
        if (!variable) {
          throw new Error(
            `Cannot find compose_schema node that outputs variable "${variableName}"`
          );
        }

        if (!variable.schemaId) {
          throw new Error(
            `The compose_schema node "${variable.nodeId}" does not have a schemaId configured`
          );
        }

        // Fetch schema definition from data service
        const schema = await schemaApi.getSchema(variable.schemaId);
        setFetchedSchema(schema);

        // Extract field names from schema
        const fields = schema.fields?.map((f) => f.name) || [];
        setSchemaFields(fields);

        if (fields.length === 0) {
          setError('Schema has no fields defined');
        }
      } catch (err: unknown) {
        console.error('Error fetching schema fields:', err);
        setError(errorUtils.getErrorMessageOrFallback(err, 'Failed to fetch schema definition'));
        setSchemaFields([]);
        setFetchedSchema(null);

        // Clear uniqueFields to prevent invalid configuration
        onChange({ uniqueFields: [] });
      } finally {
        setLoading(false);
      }
    },
    [availableVariables, onChange]
  );

  const handleDataVariableChange = useCallback(
    (value: string) => {
      onChange({
        dataVariable: value,
        uniqueFields: [], // Clear unique fields when variable changes
      });
    },
    [onChange]
  );

  const handleOperationChange = useCallback(
    (value: SchemaDataStoreConfig['operation']) => {
      const updates: Partial<SchemaDataStoreConfig> = { operation: value };

      // Clear uniqueFields if switching to insert
      if (value === 'insert') {
        updates.uniqueFields = [];
      }

      onChange(updates);
    },
    [onChange]
  );

  const handleUniqueFieldToggle = useCallback(
    (fieldName: string, checked: boolean) => {
      const currentFields = config.uniqueFields || [];
      const newFields = checked
        ? [...currentFields, fieldName]
        : currentFields.filter((f) => f !== fieldName);

      onChange({ uniqueFields: newFields });
    },
    [config.uniqueFields, onChange]
  );

  const handleResultVariableChange = useCallback(
    (value: string) => {
      onChange({ resultVariable: value || undefined });
    },
    [onChange]
  );

  const handleAddMetadataChange = useCallback(
    (checked: boolean) => {
      onChange({ addMetadata: checked });
    },
    [onChange]
  );

  const handleRetry = useCallback(() => {
    if (config.dataVariable) {
      fetchSchemaFields(config.dataVariable);
    }
  }, [config.dataVariable, fetchSchemaFields]);

  // Validation: Check if uniqueFields is required but empty
  const uniqueFieldsRequired = config.operation === 'update' || config.operation === 'upsert';
  const uniqueFieldsInvalid = uniqueFieldsRequired && (!config.uniqueFields || config.uniqueFields.length === 0);

  return (
    <div className={cn('space-y-4 p-4', className)}>
      {/* Header */}
      <div>
        <h3 className="text-sm font-semibold mb-1">Schema Data Store Configuration</h3>
        <p className="text-xs text-muted-foreground">
          Store composed schema data in the database
        </p>
      </div>

      {/* Composed Data Variable Dropdown */}
      <div className="space-y-2">
        <Label htmlFor="dataVariable" className="text-sm font-medium">
          Composed Data Variable
          <span className="text-red-500 ml-1">*</span>
        </Label>
        <Select
          value={config.dataVariable || ''}
          onValueChange={handleDataVariableChange}
        >
          <SelectTrigger id="dataVariable" className="w-full">
            <SelectValue placeholder="Select a variable..." />
          </SelectTrigger>
          <SelectContent>
            {availableVariables.length === 0 && (
              <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                No compose_schema variables found upstream
              </div>
            )}
            {availableVariables.map((v) => (
              <SelectItem key={v.name} value={v.name}>
                <div className="flex items-center gap-2">
                  <span>{v.name}</span>
                  <Badge variant="outline" className="text-[10px] px-1 py-0">
                    from {v.nodeId}
                  </Badge>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {availableVariables.length === 0 && (
          <p className="text-xs flex items-center gap-1" style={{ color: WARNING.text }}>
            <AlertCircle className="h-4 w-4" aria-hidden="true" />
            Add a compose_schema node before this node to create variables
          </p>
        )}
      </div>

      {/* Operation Dropdown */}
      <div className="space-y-2">
        <Label htmlFor="operation" className="text-sm font-medium">
          Operation
          <span className="text-red-500 ml-1">*</span>
        </Label>
        <Select
          value={config.operation || 'insert'}
          onValueChange={handleOperationChange}
        >
          <SelectTrigger id="operation" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="insert">
              <div>
                <div className="font-medium">Insert</div>
                <div className="text-xs text-muted-foreground">
                  Create new record (fail if exists)
                </div>
              </div>
            </SelectItem>
            <SelectItem value="update">
              <div>
                <div className="font-medium">Update</div>
                <div className="text-xs text-muted-foreground">
                  Update existing record (fail if not found)
                </div>
              </div>
            </SelectItem>
            <SelectItem value="upsert">
              <div>
                <div className="font-medium">Upsert</div>
                <div className="text-xs text-muted-foreground">
                  Insert or update (create if missing, update if exists)
                </div>
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Unique Fields Multi-Select (conditional) */}
      {uniqueFieldsRequired && (
        <div className="space-y-2">
          <Label className="text-sm font-medium">
            Unique Fields
            <span className="text-red-500 ml-1">*</span>
          </Label>

          {!config.dataVariable && (
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <AlertCircle className="h-4 w-4" aria-hidden="true" />
              Select a data variable first to load schema fields
            </p>
          )}

          {loading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
              <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
              Loading schema fields...
            </div>
          )}

          {error && (
            <div
              className="rounded p-4"
              style={{
                backgroundColor: ERROR.bg,
                border: `1px solid ${ERROR.border}`,
              }}
            >
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: ERROR.icon }} aria-hidden="true" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs" style={{ color: ERROR.text }}>{error}</p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleRetry}
                  className="h-6 px-2 text-xs hover:bg-red-100 focus-visible:ring-2 focus-visible:ring-offset-2"
                  style={{ color: ERROR.text, ['--tw-ring-color' as string]: FOREST_GREEN.DEFAULT }}
                >
                  <RefreshCw className="h-4 w-4 mr-1" aria-hidden="true" />
                  Retry
                </Button>
              </div>
            </div>
          )}

          {config.dataVariable && !loading && !error && schemaFields.length > 0 && (
            <div className="border rounded-md p-4 space-y-2 max-h-64 overflow-y-auto">
              {schemaFields.map((fieldName) => {
                const isChecked = config.uniqueFields?.includes(fieldName) || false;
                return (
                  <div key={fieldName} className="flex items-center gap-2">
                    <Checkbox
                      id={`field-${fieldName}`}
                      checked={isChecked}
                      onCheckedChange={(checked) =>
                        handleUniqueFieldToggle(fieldName, checked as boolean)
                      }
                      className="focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary"
                    />
                    <Label
                      htmlFor={`field-${fieldName}`}
                      className="text-sm font-normal cursor-pointer flex-1"
                    >
                      {fieldName}
                    </Label>
                  </div>
                );
              })}
            </div>
          )}

          {config.dataVariable && !loading && !error && schemaFields.length === 0 && (
            <p className="text-xs flex items-center gap-1" style={{ color: WARNING.text }}>
              <AlertCircle className="h-4 w-4" aria-hidden="true" />
              The selected schema has no fields defined
            </p>
          )}

          {uniqueFieldsInvalid && (
            <p className="text-xs flex items-center gap-1" style={{ color: ERROR.text }}>
              <AlertCircle className="h-4 w-4" aria-hidden="true" />
              Select at least one unique field for {config.operation} operation
            </p>
          )}

          {config.uniqueFields && config.uniqueFields.length > 0 && (
            <p className="text-xs text-muted-foreground">
              Selected {config.uniqueFields.length} field{config.uniqueFields.length !== 1 ? 's' : ''}
            </p>
          )}
        </div>
      )}

      {/* Add Metadata Toggle */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Checkbox
            id="addMetadata"
            checked={config.addMetadata !== false} // Default true
            onCheckedChange={handleAddMetadataChange}
            className="focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary"
          />
          <Label
            htmlFor="addMetadata"
            className="text-sm font-medium cursor-pointer"
          >
            Add Workflow Metadata
          </Label>
        </div>
        <p className="text-xs text-muted-foreground ml-6">
          Include workflow ID and execution ID in the stored record's metadata
        </p>
      </div>

      {/* Result Variable (optional) */}
      <div className="space-y-2">
        <Label htmlFor="resultVariable" className="text-sm font-medium">
          Store Result In (optional)
        </Label>
        <Input
          id="resultVariable"
          value={config.resultVariable || ''}
          onChange={(e) => handleResultVariableChange(e.target.value)}
          placeholder="storeResult"
          className="w-full"
        />
        <p className="text-xs text-muted-foreground">
          Variable name to store the operation result (ID of created/updated record)
        </p>
      </div>

      {/* Schema Info (when available) */}
      {fetchedSchema && (
        <div className="border-t pt-4 space-y-2">
          <p className="text-xs font-medium text-muted-foreground">Schema Information</p>
          <div className="text-xs space-y-0.5">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Name:</span>
              <span className="font-medium">{fetchedSchema.displayName || fetchedSchema.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Fields:</span>
              <span className="font-medium">{schemaFields.length}</span>
            </div>
            {fetchedSchema.currentVersion && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Version:</span>
                <span className="font-medium">{fetchedSchema.currentVersion}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
